/**
 * Running the grouped edits of one auto-redline output against a live context.
 *
 * Split out of `wordSequentialEditService` because group isolation is a
 * correctness constraint rather than a loop: see `applyGroups` below.
 */

import type {
  AutoRedlineOutput,
  EditApplicationResult,
  EditGroup,
} from "../../../interfaces/autoRedlineTypes";
import { createLogger } from "../../../utils/logger";
import type { ParagraphApplyContext } from "./applyContext";
import { applyBatchEdits, applySingleEdit } from "./wordBatchEditOperations";

const log = createLogger("EditGroupRunner");

/**
 * Run each group in order, appending every outcome to `results`.
 *
 * Each group is isolated: a throw from inside one is converted into failures for
 * that group's edits ONLY. Without this, a single Word throw escapes `Word.run`
 * and the caller's handler marks EVERY edit in the output as failed — including
 * edits from earlier groups that are already written into the document. That
 * wrong status is what we persist and what we send back to the model next turn.
 *
 * `applySingleEdit` catches its own failures, but the group body has throw sites
 * it cannot cover: the un-try/caught INSERT/DELETE loop and the batch commit sync
 * inside `applyBatchEdits`, and the inter-group sync below.
 *
 * `results` is mutated rather than returned so that a throw escaping even this
 * function still leaves the caller holding every outcome recorded so far.
 */
export async function applyGroups(
  groups: EditGroup[],
  operationType: AutoRedlineOutput["operationType"],
  context: Word.RequestContext,
  startTime: number,
  apply: ParagraphApplyContext,
  results: EditApplicationResult[]
): Promise<void> {
  for (let index = 0; index < groups.length; index++) {
    const group = groups[index];

    log.debug(`Executing group ${index + 1}/${groups.length}`, {
      groupType: group.isBatch ? "BATCH" : "SINGLE",
      editCount: group.edits.length,
    });

    const groupResults = await runGroup(group, operationType, context, startTime, apply, index);

    results.push(...groupResults);
    logGroupOutcome(groupResults, index);

    // Sync and log paragraph state after each group (except the last one).
    // Every handler already syncs its own work, so this is a checkpoint rather
    // than a commit — losing it must not cost us the groups already applied.
    if (index < groups.length - 1) {
      log.debug(`Syncing document state after group ${index + 1}`);

      try {
        await context.sync();
      } catch (syncError) {
        log.warn(`Inter-group sync failed after group ${index + 1}; continuing`, {
          error: syncError instanceof Error ? syncError.message : String(syncError),
        });
      }

      // Log group sync completion without extra sync
      log.debug(`Paragraph state after GROUP ${index + 1} sync`, {
        groupType: group.isBatch ? "BATCH" : "SINGLE",
        editCount: group.edits.length,
      });
    }
  }
}

/**
 * Apply one group, converting a throw into failures for its own edits.
 *
 * Later groups may well fail too if the request context itself is dead, but each
 * reports its own outcome and the groups that already applied keep theirs.
 */
async function runGroup(
  group: EditGroup,
  operationType: AutoRedlineOutput["operationType"],
  context: Word.RequestContext,
  startTime: number,
  apply: ParagraphApplyContext,
  index: number
): Promise<EditApplicationResult[]> {
  try {
    if (group.isBatch && group.edits.length > 1) {
      // Batch application for multiple edits
      log.debug(`Applying batch edits: ${group.edits.map((e) => e.editId).join(", ")}`);

      return await applyBatchEdits(group.edits, operationType, context, startTime, apply);
    }

    // Single edit application (either requiresSync or single edit in group)
    const edit = group.edits[0];

    log.debug(`Applying single edit: ${edit.editId}`);

    return [await applySingleEdit(edit, operationType, context, startTime, apply)];
  } catch (groupError) {
    const errorMsg = groupError instanceof Error ? groupError.message : String(groupError);

    log.error(`Group ${index + 1} threw; failing its edits only`, {
      error: errorMsg,
      editIds: group.edits.map((e) => e.editId),
    });

    return group.edits.map((edit) => ({
      success: false,
      editId: edit.editId,
      error: errorMsg,
      duration: Date.now() - startTime,
    }));
  }
}

/** Per-group success/failure summary for the console. */
function logGroupOutcome(groupResults: EditApplicationResult[], index: number): void {
  const successCount = groupResults.filter((r) => r.success).length;
  const failureCount = groupResults.filter((r) => !r.success).length;

  if (failureCount > 0) {
    log.warn(`Group ${index + 1} completed with failures`, {
      successCount,
      failureCount,
      failedEdits: groupResults
        .filter((r) => !r.success)
        .map((r) => ({
          editId: r.editId,
          error: r.error,
        })),
    });

    return;
  }

  log.debug(`Group ${index + 1} completed successfully`, { successCount });
}
